import { secondaryCategoryRepository } from '@webapp/core/data-access/repositories/SecondaryCategoryRepository'
import { RequestTypes } from '@webapp/core/entities/internal/enums/RequestTypeEnum'
import { buildQueryKey } from '@webapp/core/utils/react-query/buildQueryKey'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type ListApprovableTravelsParams } from '@webapp/modules/expense/business-logic/types/ListApprovableTravelsParams'
import { type TravelListStatus } from '@webapp/modules/expense/business-logic/types/TravelListStatus'
import {
  getTravelRequestFormDataUseCase,
  type GetTravelRequestFormDataParams,
} from '@webapp/modules/expense/business-logic/use-cases/GetTravelRequestFormData'
import { travelRepository } from '@webapp/modules/expense/data-access/repositories/TravelRepository'

const base = ['travel']

export const travelQueryConfig = {
  // Invalidation base for all requested-list tabs (partial-key semantics)
  requestedListBase: () => ({
    queryKey: buildQueryKey([...base, 'requested']),
  }),
  requestedList: (status: TravelListStatus, params?: RepoFilterParams) => ({
    queryKey: buildQueryKey([...base, 'requested', status, params]),
    queryFn: () => travelRepository.listRequested(status, params ?? {}),
  }),
  // Invalidation base for all approvable-list tabs (partial-key semantics)
  approvableListBase: () => ({
    queryKey: buildQueryKey([...base, 'approvable']),
  }),
  approvableList: (
    status: TravelListStatus,
    params?: ListApprovableTravelsParams
  ) => ({
    queryKey: buildQueryKey([...base, 'approvable', status, params]),
    queryFn: () => travelRepository.listApprovable(status, params ?? {}),
  }),
  // Invalidation base for all detail entries — approve/deny mutation
  // variables carry only the numeric id, not the uuid the detail query is
  // keyed by, so commands invalidate on this partial key (ADR-5 semantics).
  detailBase: () => ({
    queryKey: buildQueryKey([...base, 'detail']),
  }),
  detail: (uuid: string) => ({
    queryKey: buildQueryKey([...base, 'detail', uuid]),
    queryFn: () => travelRepository.getByUuid(uuid),
  }),
  // "Viewing as" watchlist select visibility check — the options
  // themselves load through the ADR-3 select's `loadOptions`, not a query.
  hasWatchlistApprovers: () => ({
    queryKey: buildQueryKey([...base, 'watchlist-approver-count']),
    queryFn: async () => {
      const result = await travelRepository.listWatchlistApprovers({
        page: 1,
        pageSize: 1,
      })

      return result.data.length > 0
    },
  }),
  // Hydration use case (Business layer) for the create form — sanctioned
  // hydration query inside the create-travel-request command feature.
  requestFormData: (params: GetTravelRequestFormDataParams) => ({
    queryKey: buildQueryKey([...base, 'request-form-data', params]),
    queryFn: () => getTravelRequestFormDataUseCase.execute(params),
  }),
  // Location→department cascade for the create form (travel-requestable
  // departments of the currently selected location).
  requestableDepartments: (locationId: number) => ({
    queryKey: buildQueryKey([
      ...base,
      'request-form-data',
      'departments',
      locationId,
    ]),
    queryFn: () =>
      secondaryCategoryRepository.list({
        primaryCategoryId: locationId,
        requestType: RequestTypes.TRAVEL,
        orderBy: 'name',
        pageSize: 999,
      }),
  }),
}
